import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { dropTask } from 'ember-concurrency';
import Button from 'tio-ui/components/button';
import Input from 'tio-ui/components/input';
import ErrorMessages from 'tio-ui/components/error-messages';
import type { ErrorsSignature } from 'tio-ui/components/error-messages';
import { PhUploadSimple } from 'ember-phosphor-icons';
import CategorizedUpload from 'tio-common/components/tuition-assistance/forms/categorized-upload';
import UploadAttachments from 'tio-common/components/tuition-assistance/forms/upload-attachments';
import AcceptConditions from 'tio-employee/components/tuition-assistance/forms/accept-conditions';
import { getAllValuesForTasField } from 'tio-common/utils/tuition-assistance/fields';
import { inputsForFields } from 'tio-common/utils/data/inputs-for-fields';
import SubmitEvidenceCourseRow from './submit-evidence-course-row';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasCourseExtension as TasCourseModel } from 'tio-data/data/resources/tas-course.ext';
import type { ConditionExtension as ConditionModel } from 'tio-data/data/resources/condition.ext';
import type BetaExperienceService from 'tio-employee/services/beta-experience';

export interface SubmitEvidenceBundle {
  onSaveGrade: (course: TasCourseModel, grade: string) => Promise<void>;
  onSaveGpa: (gpa: number) => Promise<void>;
  onAcceptConditions: (conditions: ConditionModel[]) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  submitError: string | ErrorsSignature | null;
  canSubmit: boolean;
}

interface SubmitEvidenceBodySignature {
  Args: {
    application: TasApplicationModel;
    /** Optional so isolated component tests can render this task without
     * wiring the full ManagePage-owned bundle; every real render passes one. */
    evidence?: SubmitEvidenceBundle;
  };
  Element: HTMLDivElement;
}

const EVIDENCE_CLASSIFICATION = 'COMPLETION_DOCUMENTS';
const noop = () => {};

/**
 * Body of the "Submit your Evidence Items" task: per-course grade entry,
 * document upload, condition agreements (template-dependent), and the real
 * submit action — mirrors the legacy show page's grade modal + attachments
 * section + AcceptConditions + submitForEvidenceApproval (see show.gts),
 * inline instead of routing out to the guided wizard. `canSubmit` (computed
 * by ManagePage) mirrors the legacy page's full canSubmitApplication gate,
 * including the eligibility lock (`inEligible`/`dynamicEligibility`, fetched
 * by manage-route.ts the same way the legacy show route fetches it).
 */
export default class SubmitEvidenceBody extends Component<SubmitEvidenceBodySignature> {
  @service('beta-experience') declare betaExperience: BetaExperienceService;
  @tracked pendingGpa = '';

  get application() {
    return this.args.application;
  }

  get programTemplate() {
    return this.application.tasProgramInstance?.tasProgramTemplate;
  }

  get activeCourses() {
    return this.application.activeCourses ?? [];
  }

  get grades() {
    const exclusiveGradeOptions = (getAllValuesForTasField(
      'EXCLUSIVE_ACADEMIC_PERFORMANCE_OPTIONS',
      this.programTemplate?.fields
    ) ?? []) as string[];
    if (exclusiveGradeOptions.length > 0) {
      return exclusiveGradeOptions;
    }
    return inputsForFields['COURSE_GRADE'].options.map((option) => option.value);
  }

  get useCategorizedUpload() {
    if (!this.betaExperience.isProductEnabled('ta')) {
      return false;
    }
    const entries = this.programTemplate?.documentEntriesFor(EVIDENCE_CLASSIFICATION) ?? [];
    return entries.length > 0;
  }

  get isAttachmentRequired() {
    return this.application.isEvidenceRequired;
  }

  get showGPAField() {
    const typeClassification = this.application.typeClassification;
    return Boolean(
      this.programTemplate?.calculateTotalByGPA &&
      (typeClassification === 'TAS.ProgramType.3' || typeClassification === 'TAS.ProgramType.4.3')
    );
  }

  get conditionsForSubmit() {
    return this.programTemplate?.applicationApproveEvidenceConditions ?? [];
  }

  updateGpa = (value: string) => {
    this.pendingGpa = value;
  };

  saveGpa = dropTask(async () => {
    if (!this.pendingGpa.trim() || !this.args.evidence) {
      return;
    }
    await this.args.evidence.onSaveGpa(Number(this.pendingGpa));
  });

  <template>
    <div class="tio-col gap-4" ...attributes>
      {{#if this.activeCourses.length}}
        <div class="tio-col gap-2">
          {{#each this.activeCourses as |course|}}
            <SubmitEvidenceCourseRow
              @course={{course}}
              @grades={{this.grades}}
              @onSave={{@evidence.onSaveGrade}}
            />
          {{/each}}
        </div>
      {{/if}}

      {{#if this.showGPAField}}
        <div class="tio-col gap-2 rounded-lg tio-bg-sunken p-3">
          <label for="evidence-gpa" class="tio-text-sm-semibold tio-fg-primary">
            {{t "tas.application.grade_point_average"}}
          </label>
          <div class="tio-row items-center gap-2">
            <Input
              @size="lg"
              type="number"
              min="0"
              max="4.0"
              step="0.01"
              id="evidence-gpa"
              data-test-evidence-gpa-input
              @value={{this.pendingGpa}}
              class="w-40"
              @onChange={{this.updateGpa}}
            />
            <Button
              @intent="primary"
              @isDisabled={{not this.pendingGpa}}
              @isRunning={{this.saveGpa.isRunning}}
              data-test-evidence-gpa-cta
              {{on "click" this.saveGpa.perform}}
            >
              {{t "common.save"}}
            </Button>
          </div>
        </div>
      {{/if}}

      {{#if this.isAttachmentRequired}}
        <div class="tio-col gap-2">
          <p class="tio-text-sm-semibold tio-fg-primary">
            {{t "tuition_assistance.applications.manage.tasks.upload_documents_label"}}
          </p>
          {{#if this.useCategorizedUpload}}
            <CategorizedUpload
              @programTemplate={{this.programTemplate}}
              @classification={{EVIDENCE_CLASSIFICATION}}
              @attachable={{this.application}}
              @attachableType="tasApplication"
              @existingAssets={{this.application.tasAssets}}
            />
          {{else}}
            <UploadAttachments
              @programTemplate={{this.programTemplate}}
              @classification={{EVIDENCE_CLASSIFICATION}}
              @attachable={{this.application}}
              @attachableType="tasApplication"
              @useDropzone={{true}}
              @attachmentListItems={{this.application.tasAssets}}
              @hideDiscarded={{true}}
            />
          {{/if}}
        </div>
      {{/if}}

      {{#if this.conditionsForSubmit.length}}
        <AcceptConditions
          @conditions={{this.conditionsForSubmit}}
          @onChange={{if @evidence.onAcceptConditions @evidence.onAcceptConditions noop}}
        />
      {{/if}}

      <div>
        <Button
          @intent="primary"
          data-test-submit-evidence-cta
          @isDisabled={{not @evidence.canSubmit}}
          @isRunning={{@evidence.isSubmitting}}
          {{on "click" (if @evidence.onSubmit @evidence.onSubmit noop)}}
        >
          <PhUploadSimple @size={{16}} class="mr-2" />
          {{t "tuition_assistance.applications.manage.tasks.submit_evidence_cta"}}
        </Button>
        {{#if @evidence.submitError}}
          <ErrorMessages
            @showErrors={{true}}
            @error={{@evidence.submitError}}
            class="text-sm mt-2"
          />
        {{/if}}
      </div>
    </div>
  </template>
}
